import { defineComponent as _defineComponent } from 'vue'
import Vue, { computed, ref, onDeactivated, onActivated, watch } from 'vue';
import type { ThumborOptions } from '@/filters/thumbor';
import { useWindowFocus } from '@vueuse/core';

import PictureComp from '@/components/picture/PictureComp.vue';
import HiddenHorizontalScrollbar from '@/components/HiddenHorizontalScrollbar.vue';

import { SnowplowTitleContextGraphql } from '@/helpers/tracking/providers';
import { useHomeModuleTracking } from '@/features/onboarded-home/tracking';
import type { Top10ModuleInYourCountry } from '@/features/onboarded-home/types';
import { usePosterImpressionTracking } from '@/helpers/composables/usePosterImpressionTracking';
import Top10TitlesHero from '@/features/jwtv-titles-rental/components/modules/TitlesHero.vue';


export default /*#__PURE__*/_defineComponent({
  __name: 'Top10JWTVModule',
  props: {
    moduleData: null
  },
  setup(__props: any) {

const props = __props as {
	moduleData: Top10ModuleInYourCountry;
};



const SCROLL_OFFSET_PX = 20;

const selectedIndex = ref(0);
const titlesContainer = ref<{ scroller: HTMLElement } | null>(null);
const isPaused = ref(false);
const animationKey = ref(0);
const selectedTitleIndex = ref(0);

const windowFocused = useWindowFocus();

const { moduleContext, moduleObserveVisibility, trackHomeModule } = useHomeModuleTracking({
	moduleData: () => props.moduleData,
	globalPosition: 0,
	localPosition: 0,
	titleSize: () => props.moduleData.titles.length,
});

const topTitles = computed(() => {
	return props.moduleData.titles ?? [];
});

function getImageUrl(posterUrl: string | undefined | null) {
	return Vue.filter('Thumbor')({
		url: posterUrl,
		imageType: 'poster',
	} as ThumborOptions);
}

function onProgressComplete() {
	if (isPaused.value) return;
	nextPoster();
}

function nextPoster() {
	selectedIndex.value = (selectedIndex.value + 1) % topTitles.value.length;
	scrollToSelectedPoster(selectedIndex.value);
	onSelectedIndexUpdate(selectedIndex.value);
}

function selectTitle(index: number) {
	// If clicking on the same poster, reset the animation
	if (selectedIndex.value === index) {
		animationKey.value++;
	} else {
		selectedIndex.value = index;
		onSelectedIndexUpdate(index);
	}

	trackHomeModule('titleSelected', {
		spec: { label: moduleContext.value.type },
		contexts: [SnowplowTitleContextGraphql.fromTitle(topTitles.value[index], { index })],
	});
}

function scrollToSelectedPoster(index: number) {
	if (!titlesContainer.value?.scroller) return;

	const element = document.getElementById(`title-poster-${index}`);
	if (!element) return;

	const container = titlesContainer.value.scroller;
	const offsetLeft = element.offsetLeft - container.offsetLeft - SCROLL_OFFSET_PX;
	const isElementHidden =
		element.offsetLeft < container.scrollLeft ||
		element.offsetLeft + element.offsetWidth > container.scrollLeft + container.offsetWidth;

	if (isElementHidden) {
		container.scrollTo({
			left: offsetLeft,
			behavior: 'smooth',
		});
	}
}

const { onVisibilityChanged } = usePosterImpressionTracking({
	groupId: 'top_10_jwtv_module',
	preventDuplicateAdditionalContext: true,
	additionalContexts: () => [moduleContext.value],
});

const selectedTitle = computed(() => {
	if (!props.moduleData || !props.moduleData.titles) return null;
	return props.moduleData.titles[selectedTitleIndex.value];
});

const selectedTitleContextModule = computed(() => {
	if (!selectedTitle.value) return null;
	return SnowplowTitleContextGraphql.fromTitle(selectedTitle.value, {
		index: selectedTitleIndex.value,
	});
});

const heroAdditionalContexts = computed(() => {
	const contexts = [moduleContext.value, selectedTitleContextModule.value];
	return contexts.filter((ctx): ctx is NonNullable<typeof ctx> => ctx != null);
});

function onSelectedIndexUpdate(index: number) {
	selectedTitleIndex.value = index;
}

function onHeroTitleClicked() {
	if (!selectedTitleContextModule.value) return;

	trackHomeModule('titleClicked', {
		spec: { label: 'more_button_clicked' },
		contexts: [selectedTitleContextModule.value],
	});
}

watch(windowFocused, focused => {
	if (!focused) {
		isPaused.value = true;
	} else {
		isPaused.value = false;
		// Reset animation when returning to the tab
		animationKey.value++;
	}
});

onDeactivated(() => {
	isPaused.value = true;
});

onActivated(() => {
	isPaused.value = false;
});

return { __sfc: true,props, SCROLL_OFFSET_PX, selectedIndex, titlesContainer, isPaused, animationKey, selectedTitleIndex, windowFocused, moduleContext, moduleObserveVisibility, trackHomeModule, topTitles, getImageUrl, onProgressComplete, nextPoster, selectTitle, scrollToSelectedPoster, onVisibilityChanged, selectedTitle, selectedTitleContextModule, heroAdditionalContexts, onSelectedIndexUpdate, onHeroTitleClicked, PictureComp, HiddenHorizontalScrollbar, Top10TitlesHero }
}

})